/**
 * The drawer's primary action row, and the forms it opens.
 *
 * Spec 02-orders is explicit about the shape of this: one click each, the reason inline
 * where it is needed, no step-up and no typed confirm. The shop has one owner working a
 * queue of payments to match, and a passkey prompt per order is how that work goes back to
 * wp-admin where nothing is audited. The API still requires the permission, the CSRF and
 * origin headers, the Idempotency-Key and the audit row; what was removed is the second
 * factor on a session that already authenticated.
 *
 * Nothing here is optimistic. Every one of these moves money or leaves the building, which
 * DESIGN.md section 6 rules out optimism for: the button spins, the server answers, the
 * drawer refetches and renders what actually happened. Optimism on a refund is how a
 * customer gets two.
 */
import { useCallback, useState } from 'react';
import type { PaymentMethod, PrintDoc, RefundReason } from '@elutide/shared';
import { Button, SelectField, TextArea, TextInput, Toggle, useToast } from '@/components';
import { useSession } from '@/app/session';
import { formatMoney, maskName } from '@/lib/format';
import { Menu, MenuItem } from '../Menu';
import { ordersApi, reprintMany } from '../api';
import { centsToInput, parseDollars } from '../money';
import type { OrderDrawerDetailView as OrderDrawerDetail } from '../types';
import { ActionPanel, ActionProblem, useAction } from './feedback';

export type PrimaryForm = 'mark_paid' | 'refund' | 'cancel' | 'status' | 'comp' | null;

/**
 * What an unmatched payment knows about itself, carried into Mark paid.
 *
 * Set when the operator pressed "This is it" on a payment with no order behind it. The
 * amount and the reference are the bank's, not a guess, and the payer name is shown so the
 * operator can see the two names side by side before committing.
 */
export interface MarkPaidPrefill {
  amount_cents: number;
  reference: string;
  payer: string | null;
  source: string;
}

export interface OrderActionsProps {
  detail: OrderDrawerDetail;
  /** Refetch the drawer and the list. Called after anything that changed the order. */
  onChanged: () => void;
  /** Which form to open on mount, for the row action that armed it. */
  armed?: PrimaryForm;
  /** The unmatched payment this order is being matched to, when there is one. */
  prefill?: MarkPaidPrefill | null;
}

const REFUND_REASONS: readonly { value: RefundReason; label: string }[] = [
  { value: 'customer_request', label: 'Customer request' },
  { value: 'damaged_in_transit', label: 'Damaged in transit' },
  { value: 'lost_in_transit', label: 'Lost in transit' },
  { value: 'wrong_item', label: 'Wrong item' },
  { value: 'duplicate_order', label: 'Duplicate order' },
  { value: 'price_adjustment', label: 'Price adjustment' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'other', label: 'Other' },
];

const VARIANCE_REASONS = [
  { value: 'accepted_short', label: 'Accepted short' },
  { value: 'overpaid_credit_issued', label: 'Overpaid, credit issued' },
  { value: 'overpaid_refunded', label: 'Overpaid, refunded' },
  { value: 'fee_deducted_by_sender', label: 'Fee deducted by sender' },
  { value: 'partial_payment_expected', label: 'Partial payment expected' },
  { value: 'other', label: 'Other' },
] as const;

const PAYMENT_METHODS: readonly { value: PaymentMethod; label: string }[] = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'cashapp', label: 'Cash App' },
  { value: 'card', label: 'Card' },
  { value: 'store_credit', label: 'Store credit' },
  { value: 'offline_other', label: 'Other offline' },
];

const OPS_STATES = [
  'created',
  'awaiting_payment',
  'paid',
  'held',
  'printed',
  'packed',
  'shipped',
  'delivered',
  'cancelled',
  'refunded',
  'failed',
] as const;

const REPRINT_CHOICES: readonly { id: string; label: string; docs: PrintDoc[] }[] = [
  { id: 'slip', label: 'Packing slip', docs: ['packing_slip'] },
  { id: 'coa', label: 'Certificate', docs: ['coa'] },
  { id: 'label', label: 'Shipping label', docs: ['label'] },
  { id: 'all', label: 'All three', docs: ['packing_slip', 'coa', 'label'] },
];

/** The $10 grant the refund form offers. Spec 02-orders, drawer primary action row. */
export const RR_CREDIT_CENTS = 1000;

export function OrderActions({ detail, onChanged, armed = null, prefill = null }: OrderActionsProps) {
  const [form, setForm] = useState<PrimaryForm>(armed);
  const [menu, setMenu] = useState<'reprint' | 'more' | null>(null);
  const action = useAction();
  const toast = useToast();
  const { can } = useSession();

  /**
   * The money actions are shown to the roles the API would accept them from.
   *
   * The API is the control; this only keeps a packer from a button that 403s. It also
   * keeps the forms honest: Mark paid and Refund read the order's total, which a packer's
   * detail does not carry (`redact.ts`), so a form a packer cannot submit is also a form
   * that would have nothing to prefill.
   */
  const expected = can('orders.mark_paid') ? detail.header.total_cents : undefined;
  const refundable =
    can('orders.refund') && detail.items.total_cents !== undefined
      ? Math.max(0, detail.items.total_cents - (detail.items.refunded_cents ?? 0))
      : undefined;
  const canMarkPaid = expected !== undefined;
  const canRefund = refundable !== undefined;
  const canCancel = can('orders.cancel');
  const canComp = can('orders.comp');

  const orderId = detail.header.order_id;
  const close = useCallback(() => {
    setForm(null);
    action.reset();
  }, [action]);

  const finish = useCallback(
    (message: string) => {
      close();
      onChanged();
      toast.push({ tone: 'ok', message });
    },
    [close, onChanged, toast],
  );

  const onReprint = useCallback(
    async (docs: readonly PrintDoc[]) => {
      setMenu(null);
      const [result] = await reprintMany([orderId], docs);
      if (!result) return;
      toast.push({
        tone: result.ok ? 'ok' : 'crit',
        message: result.ok
          ? result.replayed
            ? 'That reprint was already queued.'
            : `Sent ${result.accepted.join(', ')} to the printer.`
          : (result.error ?? 'The reprint failed.'),
      });
      onChanged();
    },
    [onChanged, orderId, toast],
  );

  const copySummary = useCallback(async () => {
    setMenu(null);
    const text = orderSummary(detail);
    try {
      await navigator.clipboard.writeText(text);
      toast.push({ tone: 'ok', message: 'Order summary copied.' });
    } catch {
      // A clipboard a browser will not give up is not an error worth a red toast, but the
      // operator still needs the text, so it goes on screen to be selected by hand.
      toast.push({ tone: 'warn', message: 'This browser would not give up the clipboard.' });
    }
  }, [detail, toast]);

  return (
    <div className="ord-actions">
      <div className="ord-actions__row">
        {canMarkPaid ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setForm(form === 'mark_paid' ? null : 'mark_paid')}
            aria-expanded={form === 'mark_paid'}
          >
            Mark paid
          </Button>
        ) : null}
        {canRefund ? (
          <Button
            size="sm"
            onClick={() => setForm(form === 'refund' ? null : 'refund')}
            aria-expanded={form === 'refund'}
          >
            Refund
          </Button>
        ) : null}
        {canCancel ? (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setForm(form === 'cancel' ? null : 'cancel')}
            aria-expanded={form === 'cancel'}
          >
            Cancel
          </Button>
        ) : null}

        <Menu
          label="Reprint"
          open={menu === 'reprint'}
          onToggle={() => setMenu((current) => (current === 'reprint' ? null : 'reprint'))}
          onClose={() => setMenu(null)}
          align="right"
        >
          {REPRINT_CHOICES.map((choice) => (
            <MenuItem
              key={choice.id}
              label={choice.label}
              onSelect={() => void onReprint(choice.docs)}
            />
          ))}
        </Menu>

        <Menu
          label="More"
          open={menu === 'more'}
          onToggle={() => setMenu((current) => (current === 'more' ? null : 'more'))}
          onClose={() => setMenu(null)}
          align="right"
        >
          <MenuItem
            label="Change status"
            hint="Any Woo status, including packed and shipped"
            onSelect={() => {
              setMenu(null);
              setForm('status');
            }}
          />
          {canComp ? (
            <MenuItem
              label="Comp order"
              hint="A zero total copy of these lines"
              onSelect={() => {
                setMenu(null);
                setForm('comp');
              }}
            />
          ) : null}
          <MenuItem label="Copy summary" onSelect={() => void copySummary()} />
        </Menu>
      </div>

      {form === 'mark_paid' && expected !== undefined ? (
        <MarkPaidForm
          detail={detail}
          expected={expected}
          action={action}
          onClose={close}
          onDone={finish}
          prefill={prefill}
        />
      ) : null}
      {form === 'refund' && refundable !== undefined ? (
        <RefundForm
          detail={detail}
          refundable={refundable}
          action={action}
          onClose={close}
          onDone={finish}
        />
      ) : null}
      {form === 'cancel' && canCancel ? (
        <CancelForm detail={detail} action={action} onClose={close} onDone={finish} />
      ) : null}
      {form === 'status' ? (
        <StatusForm detail={detail} action={action} onClose={close} onDone={finish} />
      ) : null}
      {form === 'comp' && canComp ? (
        <CompForm detail={detail} action={action} onClose={close} onDone={finish} />
      ) : null}
    </div>
  );
}

/**
 * Thrown when the refund succeeded and only the credit did not.
 *
 * It stops `useAction` reporting success, which is what keeps the panel open, and it is
 * swallowed by `ActionProblem` in favour of the more precise message above it.
 */
class CreditFailed extends Error {
  constructor() {
    super('The refund went through. The credit did not.');
    this.name = 'CreditFailed';
  }
}

type FormProps = {
  detail: OrderDrawerDetail;
  action: ReturnType<typeof useAction>;
  onClose: () => void;
  onDone: (message: string) => void;
};

/* ------------------------------------------------------------------ */
/* Mark paid                                                           */
/* ------------------------------------------------------------------ */

function MarkPaidForm({
  detail,
  expected,
  action,
  onClose,
  onDone,
  prefill,
}: FormProps & { expected: number; prefill?: MarkPaidPrefill | null }) {
  // The method the order was placed with, so the common case is one click and a reference.
  const placedWith = detail.payment.method;
  const [method, setMethod] = useState<PaymentMethod>(
    placedWith === undefined || placedWith === 'unknown' || placedWith === 'comp'
      ? 'zelle'
      : placedWith,
  );
  const [reference, setReference] = useState(prefill?.reference ?? detail.payment.reference ?? '');
  // The amount the bank actually sent, when a payment is being matched, rather than what
  // the order asked for. If they differ that is a variance, and the form is about to ask
  // for a reason rather than quietly paper over it.
  const [amount, setAmount] = useState(centsToInput(prefill?.amount_cents ?? expected));
  const [variance, setVariance] = useState<string>('');
  const [note, setNote] = useState('');

  const cents = parseDollars(amount);
  const varies = cents !== null && cents !== expected;
  const ready = reference.trim().length > 0 && cents !== null && (!varies || variance !== '');

  return (
    <ActionPanel title="Mark paid" onClose={onClose}>
      {prefill ? (
        <p className="ord-sub">
          Matching the {prefill.source} payment of{' '}
          {formatMoney(prefill.amount_cents, { showCents: true })}
          {prefill.payer ? ` from ${prefill.payer}` : ''} to {detail.customer.name}.
        </p>
      ) : null}
      <div className="ord-form">
        <SelectField
          label="Method"
          value={method}
          options={PAYMENT_METHODS}
          onChange={(event) => setMethod(event.target.value as PaymentMethod)}
        />
        <TextInput
          label="Reference"
          hint="The Zelle or Venmo confirmation, or the transaction id."
          required
          mono
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
        <TextInput
          label="Amount received"
          hint={`Expected ${formatMoney(expected, { showCents: true })}`}
          inputMode="decimal"
          mono
          value={amount}
          error={amount.trim() !== '' && cents === null ? 'Enter an amount like 312.74.' : null}
          onChange={(event) => setAmount(event.target.value)}
        />
        {varies ? (
          <SelectField
            label="Variance reason"
            hint="Required: what came in is not what was expected."
            required
            value={variance}
            options={[{ value: '', label: 'Pick a reason' }, ...VARIANCE_REASONS]}
            onChange={(event) => setVariance(event.target.value)}
          />
        ) : null}
        <TextArea
          label="Note"
          hint="Optional. Goes on the order and into the audit row."
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        <Button
          variant="primary"
          busy={action.busy}
          disabled={!ready}
          onClick={() =>
            void action
              .run(() =>
                ordersApi.markPaid(detail.header.order_id, {
                  expected_version: detail.header.version,
                  method,
                  reference: reference.trim(),
                  amount_cents: cents as number,
                  received_at: new Date().toISOString(),
                  variance_reason: varies ? (variance as never) : null,
                  note: note.trim() === '' ? null : note.trim(),
                } as never),
              )
              .then((ok) => {
                if (ok) onDone(`Order ${detail.header.order_number} is paid.`);
              })
          }
        >
          Mark paid
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Refund                                                              */
/* ------------------------------------------------------------------ */

function RefundForm({
  detail,
  refundable,
  action,
  onClose,
  onDone,
}: FormProps & { refundable: number }) {
  const [full, setFull] = useState(true);
  const [amount, setAmount] = useState(centsToInput(refundable));
  const [reason, setReason] = useState<RefundReason>('customer_request');
  const [note, setNote] = useState('');
  const [credit, setCredit] = useState(false);
  const [creditFailed, setCreditFailed] = useState<string | null>(null);
  /**
   * Whether the processor sends the money back, or a person does.
   *
   * This is WooCommerce's `api_refund`, and it is the difference between a refund and a
   * note saying there was one. A card order goes back through the gateway; Zelle, Venmo and
   * Cash App have no gateway to go back through, so the money has to be sent by hand and
   * the order is only being marked. Defaulted from the method and shown either way, because
   * getting this wrong on a card order means the order reads Refunded and the customer is
   * still out the money.
   */
  const gatewayCapable = detail.payment.method === 'card';
  const [gatewayRefund, setGatewayRefund] = useState(gatewayCapable);

  const cents = full ? refundable : parseDollars(amount);
  const overRefundable = cents !== null && cents > refundable;
  const ready = cents !== null && cents > 0 && !overRefundable;

  return (
    <ActionPanel title="Refund" onClose={onClose}>
      <div className="ord-form">
        <Toggle
          checked={full}
          label={`Refund the full ${formatMoney(refundable, { showCents: true })}`}
          onChange={setFull}
        />
        {full ? null : (
          <TextInput
            label="Amount"
            inputMode="decimal"
            mono
            value={amount}
            error={
              amount.trim() !== '' && cents === null
                ? 'Enter an amount like 20.00.'
                : overRefundable
                  ? `More than the ${formatMoney(refundable, { showCents: true })} still refundable.`
                  : null
            }
            onChange={(event) => setAmount(event.target.value)}
          />
        )}
        <SelectField
          label="Reason"
          required
          value={reason}
          options={REFUND_REASONS}
          onChange={(event) => setReason(event.target.value as RefundReason)}
        />
        <TextArea
          label="Note"
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <Toggle
          checked={gatewayRefund}
          label="Send the money back through the processor"
          onChange={setGatewayRefund}
          disabled={!gatewayCapable}
        />
        {gatewayRefund ? null : (
          <p className="ord-sub">
            {gatewayCapable
              ? 'The order will be marked refunded and no money will move. Send it by hand.'
              : `A ${detail.payment.method} payment has no gateway to refund through. Send the money by hand; this records that it happened.`}
          </p>
        )}
        <Toggle
          checked={credit}
          label="Also grant $10 Research Rewards"
          onChange={setCredit}
        />
      </div>

      {creditFailed ? (
        <p className="ord-problem" role="alert">
          The refund went through and the money is on its way back. The Research Rewards
          credit did not: {creditFailed}. Grant it from the Customer section.
        </p>
      ) : (
        <ActionProblem state={action} />
      )}

      <div className="ord-form__actions">
        <Button
          variant="danger"
          busy={action.busy}
          disabled={!ready}
          onClick={() =>
            void action
              .run(async () => {
                await ordersApi.refund(detail.header.order_id, {
                  expected_version: detail.header.version,
                  amount_cents: cents as never,
                  lines: null,
                  refund_shipping_cents: null,
                  reason,
                  note: note.trim() === '' ? null : note.trim(),
                  gateway_refund: gatewayRefund,
                  external_reference: null,
                } as never);

                if (!credit) return;
                // A separate call, and separately reportable. A credit that failed after a
                // refund that succeeded must not read as "the refund failed": the money has
                // already gone back. It must also not close the panel, or the only place
                // that says the credit did not happen unmounts before it is read.
                try {
                  setCreditFailed(null);
                  await ordersApi.credit(detail.header.order_id, {
                    amount_cents: RR_CREDIT_CENTS,
                    reason: `Refund goodwill on order ${detail.header.order_number}`,
                  });
                } catch (error) {
                  setCreditFailed(error instanceof Error ? error.message : 'the grant was refused');
                  throw new CreditFailed();
                }
              })
              .then((ok) => {
                if (ok) {
                  onDone(
                    `Refunded ${formatMoney(cents ?? 0, { showCents: true })} on order ${detail.header.order_number}.`,
                  );
                }
              })
          }
        >
          Refund {formatMoney(cents ?? 0, { showCents: true })}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Cancel                                                              */
/* ------------------------------------------------------------------ */

function CancelForm({ detail, action, onClose, onDone }: FormProps) {
  const shipped = detail.fulfilment.shipped_at !== null || detail.fulfilment.delivered_at !== null;
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(!shipped);

  return (
    <ActionPanel title="Cancel this order" onClose={onClose}>
      <p className="ord-sub">
        Cancelling asks ShipStation about the label before it writes anything. If a live
        label exists it will refuse and say so.
      </p>
      <div className="ord-form">
        <TextArea
          label="Reason"
          hint="Recorded in the audit row."
          required
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Toggle checked={restock} label="Restock the lines" onChange={setRestock} />
      </div>

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        <Button
          variant="danger"
          busy={action.busy}
          disabled={reason.trim().length === 0}
          onClick={() =>
            void action
              .run(() =>
                ordersApi.cancel(detail.header.order_id, {
                  expected_version: detail.header.version,
                  reason: reason.trim(),
                  restock,
                }),
              )
              .then((ok) => {
                if (ok) onDone(`Order ${detail.header.order_number} is cancelled.`);
              })
          }
        >
          Cancel the order
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Change status                                                       */
/* ------------------------------------------------------------------ */

function StatusForm({ detail, action, onClose, onDone }: FormProps) {
  const [state, setState] = useState<string>(detail.header.ops_state);
  const [reason, setReason] = useState('');

  return (
    <ActionPanel title="Change status" onClose={onClose}>
      <div className="ord-form">
        <SelectField
          label="Status"
          value={state}
          options={OPS_STATES.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          onChange={(event) => setState(event.target.value)}
        />
        <TextArea
          label="Reason"
          required
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        <Button
          busy={action.busy}
          disabled={reason.trim().length === 0 || state === detail.header.ops_state}
          onClick={() =>
            void action
              .run(() =>
                ordersApi.changeStatus(detail.header.order_id, {
                  expected_version: detail.header.version,
                  ops_state: state,
                  reason: reason.trim(),
                }),
              )
              .then((ok) => {
                if (ok) onDone(`Order ${detail.header.order_number} is now ${state}.`);
              })
          }
        >
          Change status
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Comp                                                                */
/* ------------------------------------------------------------------ */

function CompForm({ detail, action, onClose, onDone }: FormProps) {
  const [reason, setReason] = useState<'replacement' | 'goodwill' | 'influencer' | 'qc_sample' | 'other'>(
    'replacement',
  );
  const [note, setNote] = useState('');
  const lines = detail.items.lines.map((line) => ({
    product_id: line.product_id,
    variation_id: line.variation_id,
    qty: line.qty,
  }));
  const guest = detail.customer.customer_key === '';

  return (
    <ActionPanel title="Comp order" onClose={onClose}>
      <p className="ord-sub">
        Creates a new zero total order for {detail.customer.name} with the same{' '}
        {lines.length} line{lines.length === 1 ? '' : 's'}. Stock decrements normally.
      </p>
      <div className="ord-form">
        <SelectField
          label="Reason"
          value={reason}
          options={[
            { value: 'replacement', label: 'Replacement' },
            { value: 'goodwill', label: 'Goodwill' },
            { value: 'influencer', label: 'Influencer' },
            { value: 'qc_sample', label: 'QC sample' },
            { value: 'other', label: 'Other' },
          ]}
          onChange={(event) => setReason(event.target.value as typeof reason)}
        />
        <TextArea
          label="Note"
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        <Button
          busy={action.busy}
          disabled={guest || lines.length === 0}
          onClick={() =>
            void action
              .run(() =>
                ordersApi.comp({
                  customer_key: detail.customer.customer_key,
                  lines,
                  shipping_service: null,
                  reason,
                  note: note.trim() === '' ? null : note.trim(),
                  origin_order_id: detail.header.order_id,
                  origin_ticket_ref: null,
                } as never),
              )
              .then((ok) => {
                if (ok) onDone('Comp order created.');
              })
          }
        >
          Create the comp
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Copy summary                                                        */
/* ------------------------------------------------------------------ */

/**
 * The order in plain text, for a Slack message or an email to the customer.
 *
 * Deliberately short: what it is, what state it is in, what is in it and where it is. No
 * customer email or phone, because this is pasted into places that keep history.
 */
export function orderSummary(detail: OrderDrawerDetail): string {
  const lines = detail.items.lines.map((line) => `  ${line.qty} x ${line.name}`).join('\n');
  const tracking = detail.fulfilment.tracking_number
    ? `${detail.fulfilment.carrier.toUpperCase()} ${detail.fulfilment.tracking_number}`
    : 'no tracking yet';
  return [
    `Order ${detail.header.order_number} (${detail.header.woo_status})`,
    // Masked. DESIGN.md section 4: name plus last initial is the only customer shape
    // allowed in a Slack line, and this text exists to be pasted into one.
    // The total is absent for a packer (redact.ts) and the line simply ends at the name.
    detail.header.total_cents !== undefined
      ? `${maskName(detail.header.customer_name)}, ${formatMoney(detail.header.total_cents, { showCents: true })}`
      : maskName(detail.header.customer_name),
    `Placed ${detail.header.placed_at}`,
    lines,
    `Fulfilment: ${detail.header.ops_state}, ${tracking}`,
  ].join('\n');
}
